/**
 * SwitchPortDialogs -- the per-port "advanced" editor and the switch-level Voice VLAN / LAG
 * dialogs, moved out of routes/SwitchPorts.tsx when that route became the site-wide port TABLE
 * (2026-09-14). Behaviour is unchanged except:
 *   * the port NAME auto-syncs (owner decision 2026-09-14, option C): "Save name" stores it at once
 *     and the controller writes the switch description in the background -- it is never part of
 *     the previewed ecCLI here;
 *   * VLAN lists use the shared lib/portTable.parseVlanList;
 *   * (OP#1819) the PortEditor STARTS FROM THE PORT'S CURRENT VLANS (lib/portVlans
 *     currentTaggedSet: the switch's own read when there is one). The Tagged field is then the
 *     port's tagged SET and only the delta is sent (added -> tagged_vlans, taken out ->
 *     remove_vlans); Access removes the known tagged VLANs and says so. When the tagged list is
 *     unknown (untagged-only switch, never read) the field is "VLANs to add" as before and the
 *     editor says that VLANs already on the port are neither shown nor removed.
 * Rate limits, 802.1p priority and per-port voice VLAN live only here (not in the bulk editor).
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  usePortConfigApply, usePortPoeAction, useSetPortLabel, useSwitchVoiceVlan, useSwitchLag,
  type SwitchPortDetail, type PortChange, type PortConfigResponse,
} from "@/lib/api/hooks";
import { formatSwitchPort } from "@/lib/switchPorts";
import { parseVlanList, sanitizePortName } from "@/lib/portTable";
import { ErrorBanner } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  CommandPreview,
  OutcomeNote,
  SaveRunningConfig,
  SaveToStartupCheckbox,
} from "@/components/site/SwitchSettingsDialog";
import { describeApplyOutcome, needsSave } from "@/lib/switchApplyResult";
import { currentTaggedSet, taggedEditDelta, vlanCell, type PortVlanView } from "@/lib/portVlans";
import { NameSyncBadge } from "@/components/site/NameSyncBadge";
import { cn } from "@/lib/cn";

/**
 * PortEditor — the "Port Config" write form for one port.
 *
 * Renders the fields the backend switch_portconfig renderer understands (name, mode,
 * VLANs, PoE, admin state), then follows a strict PREVIEW -> APPLY flow: "Preview" posts a
 * dry-run and shows the EXACT ecCLI that would run; "Apply" runs it for real (backend-gated
 * by SWITCH_CLI_ENABLED). The operator never applies blind. Seeded from the port's current
 * SNMP-observed state so an unchanged field is left as-is.
 */
export function PortEditor({
  serial, port, onClose, vlans,
}: {
  serial: string; port: SwitchPortDetail; onClose: () => void;
  /** The port's VLAN view (switch/port-vlans), so the VLAN fields start from what is there. */
  vlans?: PortVlanView | null;
}) {
  /** Tagged VLANs on the port now, or null when unknown (never read / untagged-only switch). */
  const current = currentTaggedSet(vlans);
  const currentCell = vlanCell(vlans, port.native_vlan);
  const apply = usePortConfigApply(serial);
  // Names auto-sync (module header): saved immediately, never part of the previewed change.
  const saveName = useSetPortLabel(serial);
  const [name, setName] = useState(port.alias || port.descr || "");
  const nameDirty = sanitizePortName(name) !== sanitizePortName(port.alias || port.descr || "");
  const [mode, setMode] = useState<"" | "access" | "trunk">("");
  const [accessVlan, setAccessVlan] = useState<string>(port.native_vlan ? String(port.native_vlan) : "");
  const [nativeVlan, setNativeVlan] = useState<string>(port.native_vlan ? String(port.native_vlan) : "");
  // Seeded with the current tagged set when known: the field is then the port's SET.
  const [taggedVlans, setTaggedVlans] = useState<string>(current ? current.join(",") : "");
  const [removeVlans, setRemoveVlans] = useState<string>("");
  const [poe, setPoe] = useState<"" | "on" | "off">("");
  const [enabled, setEnabled] = useState<"" | "up" | "down">("");
  // Advanced (ecCLI, verified on ECS2100). "" = leave unchanged.
  const [dot1x, setDot1x] = useState<"" | "auto" | "force-authorized" | "force-unauthorized">("");
  const [priority, setPriority] = useState<string>("");
  const [lldp, setLldp] = useState<"" | "both" | "tx" | "rx" | "off">("");
  // STP edge port (spanning-tree edge-port). "" = leave unchanged. UNVERIFIED on hardware.
  const [edgePort, setEdgePort] = useState<"" | "on" | "off">("");
  const [ingressRate, setIngressRate] = useState<string>("");
  const [egressRate, setEgressRate] = useState<string>("");
  const [voiceVlan, setVoiceVlan] = useState<"" | "auto" | "none" | "vid">("");
  const [voiceVid, setVoiceVid] = useState<string>("");
  const [voicePriority, setVoicePriority] = useState<string>("");
  const [preview, setPreview] = useState<PortConfigResponse | null>(null);
  /** "Save to startup-config" -- default on (the old hard-coded behaviour), now a choice. */
  const [save, setSave] = useState(true);
  /**
   * What the current preview was rendered FROM: the exact change + save flag.
   *
   * STALE-PREVIEW FIX (S6a, 2026-09-14): Apply used to rebuild the change from the CURRENT form,
   * and editing a field after Preview did not clear the preview -- so a field nobody had
   * previewed could be applied. Now the preview is only valid while the form still renders the
   * identical request (derived below, no effect needed), and Apply sends `previewed`, never a
   * fresh buildChange().
   */
  const [previewed, setPreviewed] = useState<{ change: PortChange; save: boolean; key: string } | null>(null);
  /** The last REAL apply, with the save flag it ran with (needed to word saved:null honestly). */
  const [result, setResult] = useState<{ res: PortConfigResponse; save: boolean } | null>(null);

  // Build the structured change from the form, omitting untouched fields entirely so we only
  // ever emit CLI for what the operator actually set (matches the renderer's per-field logic).
  function buildChange(): PortChange {
    const c: PortChange = { port: port.name };
    // No `name`: it saves on its own through "Save name" and syncs to the switch automatically.
    if (mode) c.mode = mode;
    if (mode === "access" && accessVlan) c.access_vlan = Number(accessVlan);
    if (mode === "trunk" && nativeVlan) {
      c.native_vlan = Number(nativeVlan);
      // One apply does the whole native change in the hardware-verified order (server renders:
      // add N untagged, native N, tagged, then remove VLAN 1 / the OUTGOING native). Tell the
      // renderer what the port's native was so moving back to 1 can strip the old one.
      // MR !137 review: NOT when the outgoing native is being kept as tagged -- the removal would
      // take away the VLAN the operator just asked to keep (the backend filters it as well).
      if (
        port.native_vlan != null && port.native_vlan !== c.native_vlan &&
        !(mode === "trunk" && parseVlanList(taggedVlans).vlans.includes(port.native_vlan))
      ) {
        c.previous_native_vlan = port.native_vlan;
      }
    }
    // Tagged: with a KNOWN current set the field is the desired set and only the delta goes out;
    // Access takes every known tagged VLAN off. Unknown set: the field is "VLANs to add".
    const delta = taggedEditDelta(current, mode, parseVlanList(taggedVlans).vlans,
      c.native_vlan ?? c.access_vlan ?? port.native_vlan ?? null);
    if (delta.add.length) c.tagged_vlans = delta.add;
    const explicitRemovals = removeVlans.trim() ? parseVlanList(removeVlans).vlans : [];
    if (explicitRemovals.length || delta.remove.length) {
      // Never remove a VLAN this same change adds (tagged, or as the new native/access VLAN).
      const keep = new Set([...(c.tagged_vlans ?? []), c.native_vlan, c.access_vlan]);
      const removals = [...new Set([...delta.remove, ...explicitRemovals])]
        .filter((v) => !keep.has(v)).sort((a, b) => a - b);
      if (removals.length) c.remove_vlans = removals;
    }
    if (poe) c.poe = poe === "on";
    if (enabled) c.enabled = enabled === "up";
    if (dot1x) c.dot1x = dot1x;
    if (priority !== "") c.priority = Number(priority);
    if (lldp) c.lldp = lldp;
    if (edgePort) c.edge_port = edgePort === "on";
    if (ingressRate !== "") c.ingress_rate = Number(ingressRate);
    if (egressRate !== "") c.egress_rate = Number(egressRate);
    if (voiceVlan === "auto" || voiceVlan === "none") c.voice_vlan = voiceVlan;
    else if (voiceVlan === "vid" && voiceVid) c.voice_vlan = Number(voiceVid);
    if (voicePriority !== "") c.voice_vlan_priority = Number(voicePriority);
    return c;
  }

  // The request the form renders RIGHT NOW. The preview is usable only while this still equals
  // the key it was rendered from -- any edit (or an edit reverted) is caught without an effect.
  const currentKey = JSON.stringify({ change: buildChange(), save });
  const fresh = !!preview && preview.dry_run && !!previewed && previewed.key === currentKey;

  function doPreview() {
    const change = buildChange();
    const snapshot = { change, save, key: JSON.stringify({ change, save }) };
    setResult(null);
    apply.mutate(
      { changes: [change], save, dry_run: true },
      { onSuccess: (res) => { setPreview(res); setPreviewed(snapshot); } },
    );
  }
  /** Apply EXACTLY the previewed change (never a rebuild of the form). */
  function doApply() {
    if (!fresh || !previewed) return;
    const snapshot = previewed;
    apply.mutate(
      { changes: [snapshot.change], save: snapshot.save, dry_run: false },
      {
        onSuccess: (res) => {
          setResult({ res, save: snapshot.save });
          setPreview(null);
          setPreviewed(null);
          // Close only on a fully confirmed outcome; anything short of "saved" stays open so the
          // operator reads it (and can save) instead of assuming.
          if (res.ok && snapshot.save && res.saved === true) onClose();
        },
      },
    );
  }

  return (
    // Right-side slide-over drawer (Radix Dialog) that overlays the ports screen — the UniFi
    // "Port Settings" pattern. Reuses Meridian's dialog tokens (z-sheet/bg-overlay/shadow-sheet)
    // and gets focus-trap + Escape-to-close + backdrop-dismiss for free from Radix.
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-sheet flex h-full w-full max-w-md flex-col border-l border-line bg-overlay shadow-sheet outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <Dialog.Title className="text-sm font-semibold text-ink">
              Port {formatSwitchPort(port.name)} settings
            </Dialog.Title>
            <Dialog.Close
              className="rounded p-1 text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Dialog.Close>
          </div>

          {/* Scrollable body — grouped sections mirror UniFi's Port Settings IA. */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <Section title="General">
              <Field label="Name">
                <span className="flex gap-2">
                  <input
                    className={inputCls}
                    value={name}
                    maxLength={128}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="uplink to core"
                  />
                  <Button
                    variant="secondary"
                    disabled={!nameDirty || saveName.isPending}
                    onClick={() => saveName.mutate({ port: port.name, label: sanitizePortName(name) })}
                  >
                    {saveName.isPending ? "Saving…" : "Save name"}
                  </Button>
                </span>
              </Field>
              <div className="-mt-2 flex flex-col gap-1">
                <NameSyncBadge serial={serial} sync={port.name_sync} />
                <p className="text-[11px] leading-snug text-ink-subtle">Names sync to the switch automatically — not part of the preview.</p>
                {saveName.error && (
                  <p className="text-[11px] text-critical">
                    The name was not saved: {saveName.error instanceof Error ? saveName.error.message : "request failed"}
                  </p>
                )}
              </div>
              <Field label="Port state">
                <select className={inputCls} value={enabled} onChange={(e) => setEnabled(e.target.value as typeof enabled)}>
                  <option value="">(unchanged)</option>
                  <option value="up">Active (no shutdown)</option>
                  <option value="down">Disabled (shutdown)</option>
                </select>
              </Field>
              <Field label="Mode">
                <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                  <option value="">(unchanged)</option>
                  <option value="access">Access</option>
                  <option value="trunk">Trunk</option>
                </select>
              </Field>
            </Section>

            <Section title="Core settings">
              {/* What the port carries NOW (OP#1819), so an edit never starts blind. */}
              <p className="text-xs text-ink-muted" data-testid="port-editor-current-vlans" title={currentCell.title}>
                Now: untagged <span className="font-mono">{currentCell.native}</span>
                {current
                  ? current.length
                    ? <> · tagged <span className="font-mono">{current.join(" ")}</span></>
                    : " · nothing tagged"
                  : " · tagged unknown"}
                <span className="text-ink-subtle"> ({currentCell.source})</span>
              </p>
              {mode === "access" && current && current.length > 0 && (
                <p className="text-xs text-warn">
                  Access removes the tagged VLANs on this port: {current.join(", ")}.
                </p>
              )}
              {mode && !current && (
                <p className="text-xs text-warn">
                  Tagged VLANs on this port are unknown (not read from the switch): any already there are
                  neither shown nor removed.
                </p>
              )}
              {mode === "access" && (
                <Field label="Access VLAN">
                  <input className={inputCls} value={accessVlan} onChange={(e) => setAccessVlan(e.target.value)} inputMode="numeric" placeholder="100" />
                </Field>
              )}
              {mode === "trunk" && (
                <>
                  <Field label="Native VLAN (mgmt = 100)">
                    <input className={inputCls} value={nativeVlan} onChange={(e) => setNativeVlan(e.target.value)} inputMode="numeric" placeholder="100" />
                  </Field>
                  <Field label={current ? "Tagged VLANs on the port (edit the set, csv)" : "Tagged VLANs to add (csv)"}>
                    <input className={inputCls} value={taggedVlans} onChange={(e) => setTaggedVlans(e.target.value)} placeholder="601,651,699" />
                  </Field>
                  <Field label="Remove VLANs (csv)">
                    <input className={inputCls} value={removeVlans} onChange={(e) => setRemoveVlans(e.target.value)} placeholder="1" />
                  </Field>
                </>
              )}
              {!mode && (
                <p className="text-xs text-ink-subtle">Choose Access or Trunk to edit VLANs.</p>
              )}
              <Field label="PoE">
                <select className={inputCls} value={poe} onChange={(e) => setPoe(e.target.value as typeof poe)}>
                  <option value="">(unchanged)</option>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
              </Field>
            </Section>

            <Section title="Advanced">
              <Field label="802.1X port control">
                <select className={inputCls} value={dot1x} onChange={(e) => setDot1x(e.target.value as typeof dot1x)}>
                  <option value="">(unchanged)</option>
                  <option value="force-authorized">Force-authorized (always on)</option>
                  <option value="auto">Auto (802.1X)</option>
                  <option value="force-unauthorized">Force-unauthorized (block)</option>
                </select>
              </Field>
              <Field label="802.1p priority (CoS 0-7)">
                <input className={inputCls} value={priority} inputMode="numeric" placeholder="0-7"
                  onChange={(e) => setPriority(e.target.value.replace(/[^0-7]/g, "").slice(0, 1))} />
              </Field>
              <Field label="LLDP">
                <select className={inputCls} value={lldp} onChange={(e) => setLldp(e.target.value as typeof lldp)}>
                  <option value="">(unchanged)</option>
                  <option value="both">TX &amp; RX</option>
                  <option value="tx">TX only</option>
                  <option value="rx">RX only</option>
                  <option value="off">Disabled</option>
                </select>
              </Field>
              {/* STP edge port. Help text is on the control because the wrong choice (edge on a
                  switch-to-switch link) can loop the network. Command UNVERIFIED on hardware. */}
              <Field label="Edge port (spanning-tree edge-port)">
                <select className={inputCls} value={edgePort} onChange={(e) => setEdgePort(e.target.value as typeof edgePort)}>
                  <option value="">(unchanged)</option>
                  <option value="on">On (edge port)</option>
                  <option value="off">Off</option>
                </select>
              </Field>
              <p className="-mt-2 text-[11px] leading-snug text-ink-subtle">
                For ports connected to a gateway, AP or end device: the port forwards right away
                after a link change instead of waiting ~30 s. Never set it on a port that connects
                to another switch.
              </p>
              <Field label="Ingress rate limit (kbps, 0 = off)">
                <input className={inputCls} value={ingressRate} inputMode="numeric" placeholder="e.g. 100000"
                  onChange={(e) => setIngressRate(e.target.value.replace(/[^0-9]/g, ""))} />
              </Field>
              <Field label="Egress rate limit (kbps, 0 = off)">
                <input className={inputCls} value={egressRate} inputMode="numeric" placeholder="e.g. 100000"
                  onChange={(e) => setEgressRate(e.target.value.replace(/[^0-9]/g, ""))} />
              </Field>
              <Field label="Voice VLAN">
                <select className={inputCls} value={voiceVlan} onChange={(e) => setVoiceVlan(e.target.value as typeof voiceVlan)}>
                  <option value="">(unchanged)</option>
                  <option value="auto">Auto (detect phone by OUI/LLDP)</option>
                  <option value="vid">Specific VLAN…</option>
                  <option value="none">None</option>
                </select>
              </Field>
              {voiceVlan === "vid" && (
                <Field label="Voice VLAN ID">
                  <input className={inputCls} value={voiceVid} inputMode="numeric" placeholder="601"
                    onChange={(e) => setVoiceVid(e.target.value.replace(/[^0-9]/g, ""))} />
                </Field>
              )}
              {voiceVlan && voiceVlan !== "none" && (
                <Field label="Voice priority (CoS 0-7)">
                  <input className={inputCls} value={voicePriority} inputMode="numeric" placeholder="5"
                    onChange={(e) => setVoicePriority(e.target.value.replace(/[^0-7]/g, "").slice(0, 1))} />
                </Field>
              )}
            </Section>

            <Section title="Persist">
              <SaveToStartupCheckbox checked={save} onChange={setSave} />
            </Section>

            {apply.error && <div className="mb-3"><ErrorBanner error={apply.error} /></div>}

            {/* The exact ecCLI the change renders to, and the route Apply will take -- shown only
                while it still matches the form (a stale preview is never offered for Apply). */}
            {preview && !fresh && (
              <p className="mb-3 text-xs text-ink-subtle">
                The form changed after the preview — preview again before applying.
              </p>
            )}
            {fresh && preview && (
              <Section title="Preview">
                <CommandPreview preview={preview} />
              </Section>
            )}

            {/* Real apply: honest outcome, then the device transcript for troubleshooting. */}
            {result && (
              <Section title="Result">
                <OutcomeNote message={describeApplyOutcome(result.res, { saveRequested: result.save })}>
                  {needsSave(result.res) && <SaveRunningConfig serial={serial} compact />}
                </OutcomeNote>
                {result.res.transcript && (
                  <pre className="mt-2 max-h-40 overflow-auto rounded border border-line bg-surface-2 p-2 text-[0.7rem] text-ink-muted">
                    {result.res.transcript}
                  </pre>
                )}
              </Section>
            )}
          </div>

          {/* Sticky footer: preview-first, then apply exactly what was previewed. */}
          <div className="border-t border-line px-4 py-3">
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={doPreview} disabled={apply.isPending}>
                {apply.isPending && !fresh ? "Rendering…" : "Preview"}
              </Button>
              <RoleGate min="msp_admin" mode="disable" reason="Applying switch config requires admin.">
                <Button variant="primary" onClick={doApply} disabled={apply.isPending || !fresh || !!preview?.error}>
                  {apply.isPending && fresh ? "Applying…" : "Apply"}
                </Button>
              </RoleGate>
            </div>
            <p className="mt-1.5 text-xs text-ink-subtle">Preview first — Apply runs exactly the previewed commands on the switch.</p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A titled group of fields inside the port drawer (mirrors UniFi's sectioned settings). */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <h4 className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-ink-subtle">{title}</h4>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

/**
 * PoeCycleButton — one-click PoE power-cycle for a port (UniFi-parity).
 *
 * Reboots the powered device (AP/camera) by dropping then restoring PoE. Because that IS a
 * disruptive action, it confirms first, then runs for real (dry_run:false). The backend is
 * gated by SWITCH_CLI_ENABLED, so until writes are enabled it returns a clear "disabled"
 * message which we surface in the title — no silent no-op.
 */
export function PoeCycleButton({ serial, port }: { serial: string; port: string }) {
  const poe = usePortPoeAction(serial);
  const last = poe.data;
  function cycle() {
    // eslint-disable-next-line no-alert -- lightweight confirm; no modal system in this app yet.
    if (!window.confirm(`Power-cycle PoE on port ${port}? The attached device will reboot.`)) return;
    poe.mutate({ port, action: "cycle", dry_run: false });
  }
  const title = poe.isPending
    ? "Cycling…"
    : last
      ? last.ok ? "PoE cycled" : (last.error ?? "PoE cycle failed")
      : "Power-cycle PoE (reboots the attached device)";
  return (
    <button
      type="button"
      onClick={cycle}
      disabled={poe.isPending}
      title={title}
      className={cn(
        "text-ink-muted hover:text-brand disabled:opacity-50",
        last && !last.ok && "text-critical",
      )}
      aria-label={`Power-cycle PoE on port ${port}`}
    >
      ⟳ PoE
    </button>
  );
}

const inputCls = "w-full rounded border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-brand focus:outline-none";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-ink-subtle">{label}</span>
      {children}
    </label>
  );
}

/**
 * SwitchVoiceVlanDialog — switch-level (global) Voice VLAN: enable + VID + aging.
 * Preview-first (dry-run shows exact ecCLI) then Apply, same safety model as the port editor.
 * Per-port membership is set in each port's Advanced section; this is the global definition.
 */
export function SwitchVoiceVlanDialog({ serial, onClose }: { serial: string; onClose: () => void }) {
  const vv = useSwitchVoiceVlan(serial);
  const [enabled, setEnabled] = useState(true);
  const [vid, setVid] = useState("601");
  const [aging, setAging] = useState("1440");
  const [preview, setPreview] = useState<PortConfigResponse | null>(null);

  function run(dry_run: boolean) {
    vv.mutate(
      { enabled, vid: vid ? Number(vid) : undefined, aging: aging ? Number(aging) : undefined, dry_run },
      { onSuccess: (res) => { setPreview(res); if (res.ok && !res.dry_run) onClose(); } },
    );
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content aria-describedby={undefined}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-sm font-semibold text-ink">Voice VLAN (switch-wide)</Dialog.Title>
          <p className="mt-1 mb-3 text-xs text-ink-muted">
            Defines the global voice VLAN. Set per-port detection under each port&rsquo;s Advanced settings.
          </p>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Enable voice VLAN</span>
            </label>
            {enabled && (
              <>
                <Field label="Voice VLAN ID">
                  <input className={inputCls} value={vid} inputMode="numeric" placeholder="601"
                    onChange={(e) => setVid(e.target.value.replace(/[^0-9]/g, ""))} />
                </Field>
                <Field label="Aging (minutes, 5–43200)">
                  <input className={inputCls} value={aging} inputMode="numeric" placeholder="1440"
                    onChange={(e) => setAging(e.target.value.replace(/[^0-9]/g, ""))} />
                </Field>
              </>
            )}
            {vv.error && <ErrorBanner error={vv.error} />}
            {preview && (
              <pre className="max-h-40 overflow-auto rounded bg-ink/90 p-2 text-[0.7rem] leading-relaxed text-ink-invert">
                {preview.commands.join("\n")}
                {preview.error ? `\n${preview.error}` : ""}
              </pre>
            )}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="secondary" onClick={() => run(true)} disabled={vv.isPending}>Preview</Button>
            <RoleGate min="msp_admin" mode="disable" reason="Applying switch config requires admin.">
              <Button variant="primary" onClick={() => run(false)} disabled={vv.isPending || !preview}>Apply</Button>
            </RoleGate>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * SwitchLagDialog — link aggregation (port-channel / "Trunk") for one group.
 * Create (group + members + mode + load-balance) or tear down (remove). Preview-first then
 * Apply. NOTE surfaced to the operator: moving live ports into a LAG is disruptive — do it in
 * a maintenance window; the preview shows the exact ecCLI first.
 */
export function SwitchLagDialog({ serial, onClose }: { serial: string; onClose: () => void }) {
  const lag = useSwitchLag(serial);
  const [group, setGroup] = useState("1");
  const [members, setMembers] = useState("");        // "1/7, 1/8"
  const [mode, setMode] = useState<"static" | "active" | "passive">("static");
  const [loadBalance, setLoadBalance] = useState("");
  const [remove, setRemove] = useState(false);
  const [preview, setPreview] = useState<PortConfigResponse | null>(null);

  function run(dry_run: boolean) {
    const memberList = members.split(/[\s,]+/).map((m) => m.trim()).filter(Boolean);
    lag.mutate(
      { group: Number(group), members: memberList, mode, remove,
        load_balance: loadBalance || undefined, dry_run },
      { onSuccess: (res) => { setPreview(res); if (res.ok && !res.dry_run) onClose(); } },
    );
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content aria-describedby={undefined}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-sm font-semibold text-ink">Link Aggregation (LAG)</Dialog.Title>
          <p className="mt-1 mb-3 text-xs text-ink-muted">
            Bond ports into one logical trunk (ECS: up to 8 groups). Moving live ports is
            disruptive — apply in a maintenance window; preview shows the exact CLI first.
          </p>
          <div className="flex flex-col gap-3">
            <Field label="Group (1–8)">
              <input className={inputCls} value={group} inputMode="numeric" placeholder="1"
                onChange={(e) => setGroup(e.target.value.replace(/[^0-9]/g, "").slice(0, 1))} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={remove} onChange={(e) => setRemove(e.target.checked)} />
              <span>Remove this group (tear down)</span>
            </label>
            {!remove && (
              <>
                <Field label="Member ports (e.g. 1/7, 1/8)">
                  <input className={inputCls} value={members} placeholder="1/7, 1/8"
                    onChange={(e) => setMembers(e.target.value)} />
                </Field>
                <Field label="Mode">
                  <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                    <option value="static">Static (no LACP)</option>
                    <option value="active">LACP active</option>
                    <option value="passive">LACP passive</option>
                  </select>
                </Field>
                <Field label="Load balance (optional)">
                  <select className={inputCls} value={loadBalance} onChange={(e) => setLoadBalance(e.target.value)}>
                    <option value="">(switch default)</option>
                    <option value="src-dst-mac">src-dst-mac</option>
                    <option value="src-mac">src-mac</option>
                    <option value="dst-mac">dst-mac</option>
                    <option value="src-dst-ip">src-dst-ip</option>
                    <option value="src-ip">src-ip</option>
                    <option value="dst-ip">dst-ip</option>
                  </select>
                </Field>
              </>
            )}
            {lag.error && <ErrorBanner error={lag.error} />}
            {preview && (
              <pre className="max-h-40 overflow-auto rounded bg-ink/90 p-2 text-[0.7rem] leading-relaxed text-ink-invert">
                {preview.commands.join("\n")}
                {preview.error ? `\n${preview.error}` : ""}
              </pre>
            )}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="secondary" onClick={() => run(true)} disabled={lag.isPending}>Preview</Button>
            <RoleGate min="msp_admin" mode="disable" reason="Applying switch config requires admin.">
              <Button variant="primary" onClick={() => run(false)} disabled={lag.isPending || !preview}>Apply</Button>
            </RoleGate>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
